import type { ContactType, DataSource, FieldOption } from "@estelle/domain";

import type { Projection } from "@/modules/types";

/**
 * The names of the types a contact carries, for the Type column of a contact
 * list (#514).
 *
 * A `Contact` holds `typeIds`, not names, and its projection is computed from
 * the row alone, so the column cannot say "Vendor" by itself: it read `cty_0002`
 * on fixtures and `1` or `3` against the API. The screens already hold the types
 * (`contacts.listTypes()`, under the `["contacts", "types"]` key every contact
 * screen shares), so they resolve the ids here and draw the names.
 *
 * In the order the contact carries them. An id no type answers to — a type
 * deleted since, or the list not arrived yet — is left out, never drawn as the
 * raw id: a number in a column of names reads as a defect.
 */
export function typeNamesOf(
  types: readonly ContactType[] | undefined,
  typeIds: readonly string[],
): string[] {
  const named = new Map((types ?? []).map((type) => [type.id, type.name]));
  return typeIds.flatMap((id) => {
    const name = named.get(id);
    return name === undefined ? [] : [name];
  });
}

/**
 * The Type filter's choices (#533): each contact type as `{ value: id, label:
 * name }`, so the builder offers names and the chip reads one, while the stored
 * filter value stays the id the rows carry.
 */
export async function contactTypeFieldOptions(
  data: DataSource,
): Promise<Record<string, FieldOption[]>> {
  const types = await data.contacts.listTypes();
  return {
    typeIds: types.map((type) => ({ value: type.id, label: type.name })),
  };
}

/**
 * Export rows with `typeIds` written as type names (#533). A projection is
 * computed from the row alone and holds ids, so a CSV built from it read
 * `cty_0002`; the export has the types and resolves them, as the list does.
 */
export async function withTypeNames(
  data: DataSource,
  rows: Projection[],
): Promise<Projection[]> {
  const types = await data.contacts.listTypes();
  return rows.map((row) =>
    Array.isArray(row.typeIds)
      ? { ...row, typeIds: typeNamesOf(types, row.typeIds as string[]) }
      : row,
  );
}
